import cx from 'classnames';
import { FormikProvider, useFormik } from 'formik';
import { delay, isEqual, pickBy } from 'lodash';
import get from 'lodash/get';
import React, { useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import * as Yup from 'yup';
import bt from './bootstrap.module.css';
import { removeDependantFromTriggersMap } from './functions/dependants';
import Preview from './preview';
import Toolbar from './toolbar';

const validationSchema = Yup.object().shape({
  data: Yup.array().of(
    Yup.object().shape({
      label: Yup.string()
        .trim()
        .test(
          'label-length',
          'Title length must be between 3 and 512 characters',
          function (value, { parent = {}, options: { context, index } }) {
            const actualValue = get(context, ['data', index], {});
            return 'label' in parent || 'label' in actualValue
              ? value && value.length >= 3 && value.length <= 512
              : true;
          },
        ),
      text: Yup.string()
        .trim()
        .test(
          'text-length',
          'Text length must be between 3 and 2048 characters',
          function (value, { parent = {}, options: { context, index } }) {
            const actualValue = get(context, ['data', index], {});
            return 'text' in parent || 'text' in actualValue
              ? value && value.length >= 3 && value.length <= 2048
              : true;
          },
        ),
      description: Yup.string()
        .trim()
        .test(
          'description-length',
          'Description length must be between 3 and 512 characters',
          function (value, { parent = {}, options: { context, index } }) {
            const actualValue = get(context, ['data', index], {});
            return 'description' in parent || 'description' in actualValue
              ? value && value.length >= 3 && value.length <= 512
              : true;
          },
        ),
      videoLink: Yup.string()
        .trim()
        .test(
          'link-validation',
          'Invalid link',
          async function (value, { parent = {}, options: { context, index } }) {
            const actualValue = get(context, ['data', index], {});
            return 'videoLink' in parent || 'videoLink' in actualValue
              ? await Yup.string().url('Invalid url').required('Link is required').isValid(value)
              : true;
          },
        ),
      options: Yup.array()
        .of(
          Yup.object().shape({
            label: Yup.string()
              .trim()
              .min(1, 'Label length must be at least 1 character')
              .max(64, 'Too long')
              .required('Label is required'),
          }),
        )
        .when('compliance', ([compliance], schema) => {
          return compliance
            ? schema.min(2, 'At least 1 option is required')
            : schema.min(1, 'At least 1 option is required');
        })
        .max(100),
    }),
  ),
});

const moveItemAfter = (arr, firstId, secondId) => {
  const firstIndex = arr.findIndex(x => x.id === firstId);
  const secondIndex = arr.findIndex(x => x.id === secondId);

  if (firstIndex === -1 || secondIndex === -1) return arr;

  const [removed] = arr.splice(firstIndex, 1);
  const targetIndex = firstIndex < secondIndex ? secondIndex : secondIndex + 1;
  arr.splice(targetIndex, 0, removed);

  return arr;
};

export const FormBuilder = ({
  onChange,
  registers,
  imperativeRef,
  data: initData,
  onCreateRegister,
  customToolbarItems,
  isRegisterCreationLoading,
}) => {
  const formElementsEditRef = useRef();
  const toolbarRef = useRef();

  const formik = useFormik({
    initialValues: { data: initData, editElementId: null },
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema,
  });
  const editElement = formik.values.data.find(({ id }) => id === formik.values.editElementId);

  const handleToggleEditing = useCallback(
    (e, element = null) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      formik.setFieldValue('editElementId', element ? element.id : null);
    },
    [formik],
  );

  const addDataItem = useCallback(
    item => {
      const addDataItem = [...formik.values.data, item];

      formik.setFieldValue('data', addDataItem);
    },
    [formik.values.data],
  );

  const deleteDataItem = useCallback(
    item => {
      const itemToDelete =
        typeof item === 'object' ? item : formik.values.data.find(entry => entry.id === item);

      if (!itemToDelete) return;

      let deleteDataItem = formik.values.data.filter(
        entry => entry !== itemToDelete && entry.id !== itemToDelete.id,
      );

      if (itemToDelete.providerId) {
        deleteDataItem = deleteDataItem.map(entry => {
          if (entry.id !== itemToDelete.providerId) return entry;

          const dependantsIds = (entry.dependantsIds || []).filter(
            dependantId => dependantId !== itemToDelete.id,
          );

          return {
            ...entry,
            dependantsIds,
            ...removeDependantFromTriggersMap(entry, itemToDelete.id, dependantsIds),
          };
        });
      }

      formik.setFieldValue('data', deleteDataItem);
    },
    [formik.values.data],
  );

  const updateElement = useCallback(
    element => {
      const oldElement = formik.values.data.find(x => x?.id === element?.id);

      const newData = formik.values.data.map(x =>
        x?.id === element?.id ? { ...x, ...element } : x,
      );

      formik.setFieldValue('data', newData).then(() => {
        const elementIndex = formik.values.data.findIndex(x => x.id === element?.id);

        if (elementIndex === -1 || !oldElement) return;

        const diff = pickBy(element, (value, key) => !isEqual(value, oldElement[key]));

        Object.entries(diff).forEach(([key, value]) => {
          const isChangingLength = element[key]?.length !== oldElement[key]?.length;

          if (!Array.isArray(value) || (Array.isArray(value) && !isChangingLength)) {
            delay(() => {
              try {
                formik.validateField(`data[${elementIndex}].${key}`);
              } catch {
                // formik produces errors in case trying validation not described fields
              }
            }, 1);
          }
        });
      });
    },
    [formik.values.data],
  );

  const updateMultipleElements = useCallback(
    elements => {
      const newData = formik.values.data.map(x => {
        const element = elements.find(el => el.id === x.id);

        return element || x;
      });

      formik.setFieldValue('data', newData);
    },
    [formik.values.data],
  );

  const bulkManipulation = useCallback(
    ({ itemsToAdd = [], itemsToUpdate = [], itemsToDelete = [] }) => {
      const afterDeleteData = formik.values.data.filter(x => !itemsToDelete.includes(x.id));
      let newData = [...afterDeleteData, ...itemsToAdd].map(x => {
        const element = itemsToUpdate.find(el => el.id === x.id);

        return element || x;
      });

      if (
        (itemsToAdd.length === 1 &&
          itemsToUpdate.length === 1 &&
          itemsToAdd?.[0]?.providerId === itemsToUpdate?.[0]?.id) ||
        itemsToUpdate.length === 2
      ) {
        //sort dependant after parent ignore dragdrop
        const first =
          itemsToAdd.length === 1 && itemsToUpdate.length === 1 ? itemsToAdd[0] : itemsToUpdate[0];
        const second =
          itemsToAdd.length === 1 && itemsToUpdate.length === 1
            ? itemsToUpdate[0]
            : itemsToUpdate[1];

        newData = moveItemAfter(newData, first.id, second.id);
      }

      formik.setFieldValue('data', newData);
    },
    [formik.values.data],
  );

  const swapElements = useCallback(
    (firstIndex, secondIndex) => {
      const dataWithoutParents = formik.values.data.filter(x => !x.parentId);
      const dataWithParents = formik.values.data.filter(x => !!x.parentId);

      const first = dataWithoutParents[firstIndex];
      const second = dataWithoutParents[secondIndex];

      const newData = [
        ...dataWithoutParents.with(firstIndex, second).with(secondIndex, first),
        ...dataWithParents,
      ];

      formik.setFieldValue('data', newData);
    },
    [formik.values.data],
  );

  useEffect(() => {
    onChange && onChange(formik.values.data);
  }, [formik.values.data]);

  useImperativeHandle(
    imperativeRef,
    () => ({
      validate: async () => {
        const errors = await formik.validateForm(formik.values);

        return !get(errors, 'data', []).filter(Boolean).length;
      },
    }),
    [formik],
  );

  const clickListener = useCallback(event => {
    const { target, clientX, clientY } = event;
    const root = document.getElementById('root');

    if (!formElementsEditRef.current || !toolbarRef.current || !root || !root.contains(target)) {
      return;
    }

    const { height, width } = formElementsEditRef.current.getBoundingClientRect();

    const isFormElementsEditClick = clientX <= width && clientY <= height;
    const isNestedToolbarClick = toolbarRef.current.contains(target);

    if (isFormElementsEditClick || isNestedToolbarClick) return;

    formik.setFieldValue('editElementId', null);
  }, []);

  useEffect(() => {
    document.addEventListener('click', clickListener);

    return () => {
      document.removeEventListener('click', clickListener);
    };
  }, []);

  return (
    <DndProvider backend={HTML5Backend} context={window}>
      <FormikProvider value={formik}>
        <div className={cx('react-form-builder', bt.clearfix, bt['h-100'])}>
          <Preview
            formElementsEditRef={formElementsEditRef}
            registers={registers}
            onToggleEditing={handleToggleEditing}
            parent={this}
            editElement={editElement}
            onSwapElements={swapElements}
            onUpdateElement={updateElement}
            onUpdateMultipleElements={updateMultipleElements}
            onBulkManipulation={bulkManipulation}
            onAddItem={addDataItem}
            onDeleteItem={deleteDataItem}
            onCreateRegister={onCreateRegister}
            isRegisterCreationLoading={isRegisterCreationLoading}
          />
          <Toolbar
            _ref={toolbarRef}
            onAddItem={addDataItem}
            registers={registers}
            customItems={customToolbarItems}
          />
        </div>
      </FormikProvider>
    </DndProvider>
  );
};

export default FormBuilder;
